/**
 * SharePostModal — web port of the mobile share bottom sheet.
 *
 * Copy Link only touches the clipboard. Native Web Share opens the browser's
 * share sheet with the share link without creating a duplicate reshare post.
 */
import { useContext, useState } from 'react';
import groupContext from '@wlb/shared/context/GroupContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { buildSharePostUrl } from '@wlb/shared/config/env';
import { copyWithToast } from '@wlb/shared/utils/clipboardHelper';
import { canSharePost } from '@wlb/shared/utils/postSharing';
import type { FeedPost } from '@wlb/shared/types/post';
import { Modal } from '../../components/ui/Modal';
import { LinkIcon, ShareIcon } from '../../components/ui/icons';
import './feedModal.css';

interface SharePostModalProps {
  post: FeedPost;
  onClose: () => void;
}

export function SharePostModal({ post, onClose }: SharePostModalProps) {
  const { t } = useTranslation();
  const group = useContext(groupContext);
  const [sharing, setSharing] = useState(false);

  if (!canSharePost(post)) {
    return null;
  }

  const shareUrl = buildSharePostUrl(post.id);

  const copyLink = () => {
    copyWithToast(shareUrl, t('profile.toast.copied'));
    onClose();
  };

  const handleShare = async () => {
    if (sharing) return;
    if (!navigator.share) {
      copyLink();
      return;
    }

    setSharing(true);
    try {
      await navigator.share({
        title: t('bottomSheet.nativeShareTitle'),
        text: t('bottomSheet.sharePostTemplate', {
          username: post.username,
          message: post.text || t('bottomSheet.fallbackShareMessage'),
        }),
        url: shareUrl,
      });
      if (post.groupId) {
        group?.updateGroupShareCount(post.id);
      }
    } catch {
      // Cancelled or unavailable — ignore
    } finally {
      setSharing(false);
      onClose();
    }
  };

  return (
    <Modal
      title={t('bottomSheet.sharePost')}
      onClose={sharing ? () => undefined : onClose}
      labelledBy="share-post-title"
      className="share-post-modal"
    >
      <div className="share-post">
        <button type="button" className="share-post__option" onClick={copyLink}>
          <span className="share-post__icon" aria-hidden>
            <LinkIcon size={24} />
          </span>
          {t('bottomSheet.copyLink')}
        </button>
        <button
          type="button"
          className="share-post__option"
          onClick={() => { handleShare().catch(() => undefined); }}
          disabled={sharing}
          aria-busy={sharing}
        >
          <span className="share-post__icon" aria-hidden>
            <ShareIcon size={24} />
          </span>
          {t('bottomSheet.share')}
        </button>
      </div>
    </Modal>
  );
}
